import React, {Fragment, useEffect} from 'react';
import Slider, {SliderAction} from '@personalization/scripts/components/Slider.jsx';
import WishlistHeartRecoWrapper from '@wishlist/components/WishlistHeartRecoWrapper.jsx';
import MessageBroker from '@bonprix/pattern-library/components/message-broker';
import ProductMessages from '../message-list';
import PointOfSaleId from '@core/scripts/point-of-sale-id';
import SliderLandmarkHelper from '@core/scripts/components/tracking/landmarks/slider-landmark-helper';
import RecommendationUtils from './recommendation-utils';
import SliderRecommendationItems from '@personalization/scripts/components/SliderRecommendationItems.jsx';
import {Carousel, Typography} from '@bonprix/design-system';
import ProductCard, {Touchpoint} from '@wishlist/components/product-card/ProductCard.jsx';
import onceVisible from '@core/scripts/helper/once-visible';
import {SliderTypes} from './slider-types.js';

export default function ProductSeries({
                                          className = 'recommendations',
                                          sourceItem,
                                          sourceStyle,
                                          shopId,
                                          exclusionIds,
                                          productSeries,
                                          componentRef,
                                          numberOfItemsPerPage,
                                          gap,
                                          fullWidth,
                                          carouselPadding,
                                          showArrows,
                                      }) {
    useEffect(() => {
        if (productSeries?.recommendations && componentRef?.current) {
            onceVisible(
                componentRef.current,
                () => {
                    onIsVisible({items: productSeries.recommendations, displayedItemCount: numberOfItemsPerPage});
                },
                1
            );
        }

        const subscription = MessageBroker.subscribe(ProductMessages.CHANGED, ({item}) => {
            if (item) {
                SliderAction.changeSlideTo({
                    sliderId: 'product-series',
                    slideIndex: 0,
                });
            }
        });

        return () => MessageBroker.unsubscribe(subscription);
    }, []);

    const sortProductSeriesProducts = (products) => {
        if (!products) {
            return null;
        }

        const sourceItemIdProducts = [];
        const remainingProducts = [];
        const sameStyleRecommendationProducts = products.filter((product) => {
            return (
                (product.sourceStyleName === sourceStyle.name || product.sourceStyleName === undefined) &&
                !exclusionIds.some((id) => id === product.itemId)
            );
        });
        const productsWithSameSourceItemId = sameStyleRecommendationProducts.filter(
            (product) => product.sourceItemId === sourceItem.id
        );
        const productsWithDifferentSourceItemId = sameStyleRecommendationProducts.filter(
            (product) => product.sourceItemId !== sourceItem.id
        );

        productsWithSameSourceItemId.forEach((product) => {
            const notInSourceItemProducts = !sourceItemIdProducts.some(
                (item) => item.itemId === product.itemId
            );
            if (notInSourceItemProducts) {
                sourceItemIdProducts.push(product);
            }
        });
        productsWithDifferentSourceItemId.forEach((product) => {
            const notInSourceItemIdProducts = !sourceItemIdProducts.some(
                (item) => item.itemId === product.itemId
            );
            const notInRemainingProducts = !remainingProducts.some(
                (item) => item.itemId === product.itemId
            );
            if (notInSourceItemIdProducts && notInRemainingProducts) {
                remainingProducts.push(product);
            }
        });

        return sourceItemIdProducts.concat(remainingProducts).slice(0, 16);
    };

    const landmarkData = {
        pageType: 'adp',
        type: 'article_to_article',
        pointOfSaleId: PointOfSaleId.PRODUCT_SERIES,
        shopId,
    };

    const onSlideChanged = () => {
        SliderLandmarkHelper.createSendSlideChangedLandmark(landmarkData, fullWidth);
    };

    const onSlideClicked = (clickedItem) => {
        const {productImageId, itemNumber, productDigistyleId, availabilityStatus} = clickedItem;
        SliderLandmarkHelper.createAndSendSlideClickedLandmark(
            SliderTypes.PRODUCT_SERIES,
            productImageId,
            itemNumber,
            productDigistyleId,
            availabilityStatus,
            landmarkData
        );
    };

    const onLinkClicked = (clickedItem) => {
        RecommendationUtils.navigateToTargetWithRecommendationTypeOrigin('productseries', clickedItem.productUrl);
    };

    const onProductCardClicked = (event, product) => {
        if (event.target.closest('a')) {
            event.preventDefault();
            const {assetId, articleNumber, digistyleId, soldOut} = product;
            const availabilityStatus = soldOut ? 'SOLDOUT' : 'AVAILABLE';
            SliderLandmarkHelper.createAndSendSlideClickedLandmark(
                SliderTypes.PRODUCT_SERIES,
                assetId,
                articleNumber,
                digistyleId,
                availabilityStatus,
                landmarkData);
            RecommendationUtils.navigateToTargetWithRecommendationTypeOrigin('productseries', product.productLink);
        }
    };

    const onIsVisible = (event) => {
        SliderLandmarkHelper.createAndSendSliderVisibilityLandmark(SliderTypes.PRODUCT_SERIES, event, landmarkData, fullWidth);
    };

    if (fullWidth) {
        const {title, recommendations} = productSeries;
        const sortedProductSeries = sortProductSeriesProducts(recommendations);

        if (sortedProductSeries?.length === 0) {
            return null;
        }

        return (
            <>
                <div className={"product-recommendations-with-spacing"} ref={componentRef}>
                    <div className="mb-4">
                        <Typography variant="bonprix-h4" component="div" align='center'>
                            {title}
                        </Typography>
                    </div>
                    <Carousel label="label"
                              gap={gap}
                              arrows={showArrows}
                              pagination={false}
                              padding={{
                                  right: `${carouselPadding}px`,
                              }}
                              perPage={numberOfItemsPerPage}
                              perMove={numberOfItemsPerPage}
                              onMove={onSlideChanged}
                    >
                        {sortedProductSeries.map((product, index) => (
                            <Carousel.Slide key={'product-series-carousel-' + index}>
                                <div onClick={(e) => onProductCardClicked(e, product)}>
                                    <ProductCard item={product} touchpoint={Touchpoint.RECOMMENDATION}/>
                                </div>
                            </Carousel.Slide>
                        ))}
                    </Carousel>
                </div>
            </>
        );
    }

    const {recommendationsTitle, multiplePricesText, recommendationProducts} = productSeries;
    const sortedProductSeries = sortProductSeriesProducts(recommendationProducts);

    return (
        <>
            <div className={className} ref={componentRef}>
                <Slider
                    id={'product-series'}
                    items={sortedProductSeries}
                    title={recommendationsTitle}
                    onIsVisible={onIsVisible}
                    onSlideChanged={onSlideChanged}
                >
                    <SliderRecommendationItems
                        className={className}
                        items={sortedProductSeries}
                        multiplePricesText={multiplePricesText}
                        onSlideClicked={onSlideClicked}
                        onLinkClicked={onLinkClicked}
                    >
                        <WishlistHeartRecoWrapper/>
                    </SliderRecommendationItems>
                </Slider>
            </div>
            {sortedProductSeries.length > 0 && <div className="product-recommendations__divider"/>}
        </>
    );
}
